import { apiFetch } from './apiClient.js';
import { config } from '../config.js';
import type { EditorFlow } from '@zengine/flow-editor-ui';

export interface FlowSummary {
  flowId: string;
  channel: string;
  version: string;
  updatedAt?: string;
}

export async function listFlows(tenantId: string): Promise<FlowSummary[]> {
  const res = await apiFetch<{ items: FlowSummary[] }>(
    `/v1/flows?tenantId=${encodeURIComponent(tenantId)}`,
    { baseUrl: config.apiBaseUrlFlow },
  );
  return res.items;
}

export async function getFlow(channel: string, tenantId?: string): Promise<EditorFlow> {
  const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  return apiFetch<EditorFlow>(`/v1/flows/${encodeURIComponent(channel)}${qs}`, {
    baseUrl: config.apiBaseUrlFlow,
  });
}

export async function publishFlow(channel: string, flow: Omit<EditorFlow, 'channel'>): Promise<EditorFlow> {
  return apiFetch<EditorFlow>(`/v1/flows/${encodeURIComponent(channel)}`, {
    method: 'PUT',
    body: flow,
    baseUrl: config.apiBaseUrlFlow,
  });
}

/**
 * Autoguardado del builder. El borrador vive aparte del flow publicado: se
 * guarda sin validar el schema, porque un flow a medio armar no valida.
 */
export async function saveDraft(channel: string, draft: unknown): Promise<{ savedAt: string }> {
  return apiFetch<{ savedAt: string }>(`/v1/flows/${encodeURIComponent(channel)}/draft`, {
    method: 'PUT',
    body: draft as Record<string, unknown>,
    baseUrl: config.apiBaseUrlFlow,
  });
}

export async function getDraft(
  channel: string,
): Promise<{ draft: unknown; savedAt: string } | null> {
  try {
    return await apiFetch<{ draft: unknown; savedAt: string }>(
      `/v1/flows/${encodeURIComponent(channel)}/draft`,
      { baseUrl: config.apiBaseUrlFlow },
    );
  } catch (err) {
    // No tener borrador es lo normal: se abre el editor y se edita lo publicado.
    if (/404|not.?found/i.test(String((err as Error)?.message ?? ''))) return null;
    throw err;
  }
}

export async function discardDraft(channel: string): Promise<void> {
  await apiFetch<void>(`/v1/flows/${encodeURIComponent(channel)}/draft`, {
    method: 'DELETE',
    baseUrl: config.apiBaseUrlFlow,
  });
}

export async function deleteFlow(channel: string, tenantId?: string): Promise<void> {
  const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  await apiFetch<void>(`/v1/flows/${encodeURIComponent(channel)}${qs}`, {
    method: 'DELETE',
    baseUrl: config.apiBaseUrlFlow,
  });
}

export async function activateFlow(channel: string, tenantId?: string): Promise<{ activated: boolean; flowId: string; version: string }> {
  const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  return apiFetch<{ activated: boolean; flowId: string; version: string }>(
    `/v1/flows/${encodeURIComponent(channel)}/activate${qs}`,
    { method: 'POST', baseUrl: config.apiBaseUrlFlow },
  );
}

export async function createFlowScaffold(
  channel: string,
  tenantId: string,
  opts: { name?: string } = {},
): Promise<EditorFlow> {
  const scaffold: Omit<EditorFlow, 'channel'> = {
    id: `flow-${channel}-${Date.now()}`,
    tenantId,
    version: '1.0.0',
    startStepId: 'welcome',
    steps: [
      {
        id: 'welcome',
        type: 'text',
        text: opts.name
          ? `Hola, bienvenido a ${opts.name}. ¿En qué puedo ayudarte?`
          : 'Hola, bienvenido. ¿En qué puedo ayudarte?',
        next: null,
      },
    ],
  };
  return publishFlow(channel, scaffold);
}
